import React from "react";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { MantineProvider } from "@mantine/core";

import useAuthStore from "./store/authStore";
import {
    applyAuthHeader,
    getAccessToken,
    getStoredUser,
    isAccessTokenExpired,
    isAuthApiError,
    isAuthSessionEndpoint,
    refreshAccessTokenOnce,
} from "./utils/authSession";
import { API_ERROR, parseApiResponse } from "./utils/apiParams";
import {
    checkAppVersionIfStale,
    cleanReloadTimeParam,
    handleAppVersionFromResponse,
} from "./utils/appVersion";

import RoutesList from "./components/routes.list.component";
import Preloader from "./components/preloader/preloader.component";
import ToTopButton from "./components/to_top_button/to.top.button.component";
import Notif from "./components/notif/notif.component";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "./styles/App.scss";

/** Ответ API с ошибкой сервера (error 2): только для адресов php/ сайта, чужие адреса (картинки по ссылке) не трогаются. */
const isServerApiError = (response) =>
    (response?.config?.url || "").includes("php/") &&
    response?.data &&
    typeof response.data === "object" &&
    Number(response.data.error) === API_ERROR.SERVER;

/**
 * Страницы показывают окно с error_text при error 1 (ошибка проверки), а ошибку сервера (error 2) не проверяют
 * и принимали её за успех. Для ответов HTTP 200 ошибка сервера показывается теми же окнами: код меняется на 1.
 */
const showServerErrorAsValidation = (response) => {
    if (isServerApiError(response)) {
        response.data.error = API_ERROR.VALIDATION;
    }
};

/** Тексты ошибок сервера, которыми перехватчик отклонил запросы с HTTP 500 (обычно один общий текст). */
const serverErrorTexts = new Set();

const rejectWithServerError = (text) => {
    serverErrorTexts.add(text);

    return Promise.reject(text);
};

/**
 * Окно с текстом ошибки сервера, если страница не обработала отклонение запроса с HTTP 500 сама.
 * Своё состояние у отдельного компонента: состояние в App перемонтировало бы все страницы.
 */
const ServerErrorNotif = () => {
    const [text, setText] = React.useState("");

    React.useEffect(() => {
        const onUnhandledRejection = (event) => {
            if (
                typeof event.reason === "string" &&
                serverErrorTexts.has(event.reason)
            ) {
                event.preventDefault();
                setText(event.reason);
            }
        };

        window.addEventListener("unhandledrejection", onUnhandledRejection);

        return () =>
            window.removeEventListener(
                "unhandledrejection",
                onUnhandledRejection,
            );
    }, []);

    if (!text) return null;

    return (
        <Notif
            title="Ошибка"
            text={text}
            state="error"
            opened={true}
            onClose={() => setText("")}
        />
    );
};

const App = () => {
    // Только функции стора: подписка на весь стор перерисовывала App при любом изменении пользователя
    // (новое фото, тихий refresh токена), а AnimatedPageTransition ниже при этом монтировал всё дерево заново
    // и страницы теряли своё состояние (введённые данные, окна с ошибкой)
    const setUser = useAuthStore((state) => state.setUser);
    const logout = useAuthStore((state) => state.logout);
    const restoreSession = useAuthStore((state) => state.restoreSession);

    const [timer, setTimer] = React.useState(1500);
    const [app, setApp] = React.useState(false);

    const fetchData = async () => {
        window.localStorage.removeItem("user");
        applyAuthHeader();

        axios.interceptors.response.use(
            async (response) => {
                if (typeof response.data === "string") {
                    response.data = parseApiResponse(response.data);
                }

                handleAppVersionFromResponse(response);

                const requestUrl = response.config?.url || "";

                if (
                    isAuthApiError(response?.data) &&
                    !isAuthSessionEndpoint(requestUrl)
                ) {
                    const originalRequest = response.config;

                    if (!originalRequest._retry) {
                        originalRequest._retry = true;
                        const user = await refreshAccessTokenOnce();

                        if (user) {
                            setUser(user);
                            originalRequest.headers =
                                originalRequest.headers || {};
                            originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
                            applyAuthHeader();
                            return axios(originalRequest);
                        }
                    }

                    const token = getAccessToken();
                    const stored = getStoredUser();

                    if (
                        token &&
                        stored &&
                        !isAccessTokenExpired(token) &&
                        !originalRequest._tokenRetry
                    ) {
                        originalRequest._tokenRetry = true;
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        applyAuthHeader();
                        return axios(originalRequest);
                    }

                    await logout();
                    return Promise.reject(
                        new Error(response?.data?.error_text || "auth"),
                    );
                }

                showServerErrorAsValidation(response);

                return response;
            },
            (error) => {
                const response = error?.response;

                // Фатальная ошибка PHP приходит с HTTP 500 и JSON-телом. Запрос по-прежнему отклоняется: страницы,
                // которые не проверяют error, иначе приняли бы сбой за успех. Отклонение несёт текст сервера
                // вместо «Request failed with status code 500»; если страница его не обработала, текст
                // показывает ServerErrorNotif
                if (response) {
                    response.data = parseApiResponse(response.data);

                    if (isServerApiError(response) && response.data.error_text) {
                        return rejectWithServerError(response.data.error_text);
                    }
                }

                return Promise.reject(error.message);
            },
        );

        cleanReloadTimeParam();

        try {
            await checkAppVersionIfStale(window.global.baseUrl);
            await restoreSession();
        } catch (e) {
            console.error("restoreSession:", e);
        } finally {
            setApp(true);
            setTimer(0);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const AnimatedPageTransition = ({ children }) => {
        return (
            <AnimatePresence mode="wait">
                <Preloader loading={timer > 0}>{children}</Preloader>
            </AnimatePresence>
        );
    };

    return (
        <MantineProvider>
            {app && (
                <BrowserRouter>
                    <AnimatedPageTransition>
                        <RoutesList />
                        <ToTopButton />
                    </AnimatedPageTransition>
                </BrowserRouter>
            )}
            <ServerErrorNotif />
        </MantineProvider>
    );
};

export default App;
